/** sessionStorage key — only set when hitting a protected route without a session */
export const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect';

/**
 * Query when login/register was opened from LoginRequired (survives refresh; pairs with state).
 */
export const AUTH_REQUIRED_SEARCH = '?auth=required';

/** True if this visit is part of the “must log in first” flow (guard or refresh on that URL). */
export function isAuthRequiredEntry(location: {
  pathname: string;
  search: string;
  state?: unknown;
}): boolean {
  try {
    if (new URLSearchParams(location.search).get('auth') === 'required') {
      return true;
    }
  } catch {
    // ignore
  }
  if (
    typeof location.state === 'object' &&
    location.state !== null &&
    (location.state as { authRequired?: boolean }).authRequired === true
  ) {
    return true;
  }
  return false;
}

export function setPostLoginRedirectFromLocation(
  pathname: string,
  search: string,
  hash: string,
): void {
  try {
    const target = `${pathname}${search}${hash}`;
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, target);
  } catch {
    // quota / private mode
  }
}

export function clearPostLoginRedirect(): void {
  try {
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  } catch {
    // ignore
  }
}

/** Reject open redirects: only same-origin relative paths starting with a single `/`. */
export function isSafeInternalPath(value: string): boolean {
  if (!value || typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed.startsWith('/')) return false;
  if (trimmed.startsWith('//')) return false;
  if (trimmed.includes('://')) return false;
  if (/[\r\n]/.test(trimmed)) return false;
  return true;
}

/** Read, validate, remove. Returns null if missing or unsafe. */
export function consumePostLoginRedirect(): string | null {
  try {
    const raw = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    if (!raw || !isSafeInternalPath(raw)) return null;
    return raw;
  } catch {
    return null;
  }
}
